import { Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { User } from 'src/app/model/user';
import { UserService } from 'src/app/service/user.service';
import { AuthService } from 'src/app/service/auth.service';
import { HelpdeskOverlayService } from 'src/app/service/helpdesk-overlay.service';

/**
 * Uma seção da área de administração global (`/admin`). Cada seção é uma rota
 * própria sob `/admin` (ver app-routing.module.ts) e um item clicável no menu
 * lateral. A mesma lista alimenta tanto o `AdminSidebarComponent` (os itens)
 * quanto o `AdminLandingComponent` (o título da seção ativa no header do
 * conteúdo) — single source of truth pra não dessincronizar rótulo/ícone.
 */
export interface AdminSection {
  /** Segmento de rota sob `/admin` (ex.: 'events'). */
  path: string;
  /** Link absoluto pronto pro `routerLink` (ex.: '/admin/events'). */
  link: string;
  /** Material Symbols Outlined — nome como conteúdo do `<mat-icon>`. */
  icon: string;
  /** Chave i18n do rótulo (reaproveita as legendas das abas originais). */
  label: string;
  /** Quando true, a página da seção renderiza seu PRÓPRIO header (ícone +
   *  título + controles, padrão "Lista de submissões"), então o shell
   *  (AdminLandingComponent) NÃO mostra o título genérico. Setado conforme as
   *  páginas vão sendo modernizadas. */
  ownHeader?: boolean;
}

/**
 * As 8 seções que antes eram abas do `mat-tab-group` em
 * `admin/landing/landing.component.html`. Ordem preservada. Rótulos = as
 * MESMAS chaves i18n das abas; ícones em Material Symbols Outlined (mesma
 * fonte/idioma visual do menu lateral do layout moderno).
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  { path: 'events', icon: 'event', label: 'admin.events.title', ownHeader: true },
  { path: 'event-requests', icon: 'pending_actions', label: 'admin.event-requests.title', ownHeader: true },
  { path: 'email-templates', icon: 'mail', label: 'admin.email-template.default-email-templates-title', ownHeader: true },
  { path: 'administrators', icon: 'admin_panel_settings', label: 'admin.administrators.title', ownHeader: true },
  { path: 'user-search', icon: 'person_search', label: 'admin.user-search.title', ownHeader: true },
  { path: 'merge-users', icon: 'merge', label: 'admin.merge-users.title', ownHeader: true },
  { path: 'proxy-history', icon: 'history', label: 'admin.proxy-history.title', ownHeader: true },
  { path: 'deleted-submissions', icon: 'delete', label: 'admin.deleted-submissions.title', ownHeader: true },
].map(s => ({ ...s, link: `/admin/${s.path}` }));

@Component({
  selector: 'app-admin-sidebar',
  templateUrl: './admin-sidebar.component.html',
  styleUrls: ['./admin-sidebar.component.scss']
})
export class AdminSidebarComponent implements OnInit, OnDestroy {
  /** Itens renderizados no menu — a lista canônica de seções do `/admin`. */
  readonly sections = ADMIN_SECTIONS;

  user: User;
  userIsAdmin = false;
  /** Espelha o estado do card de helpdesk (preenche o ícone via `.menu-open`). */
  isSupportCardOpen = false;
  // `imageAvatarUrl` pode apontar pra uma foto que existe no banco mas falha
  // ao carregar (404, arquivo removido, etc.) — sem isso o <img> ficava com o
  // ícone de imagem quebrada em vez de cair pro ícone de fallback.
  imageError = false;

  // ─── Resize do menu (mesmo padrão do `.experimental-sidebar` moderno) ───
  // Largura persistida em localStorage, aplicada via CSS var no host (a coluna
  // `.admin-shell-nav` lê `flex-basis: var(--admin-sidebar-width)`).
  static readonly MIN_WIDTH = 200;
  static readonly MAX_WIDTH = 420;
  static readonly STORAGE_KEY = 'jems3-admin-global-sidebar-width';
  width = 240;
  isResizing = false;
  isHoveringResizeHandle = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  private subs: Subscription[] = [];

  constructor(
    private el: ElementRef<HTMLElement>,
    private userService: UserService,
    private authService: AuthService,
    private helpdeskOverlay: HelpdeskOverlayService,
    private ngZone: NgZone,
  ) { }

  /** Listeners attached only durante o drag do resize — quando não estamos
   *  arrastando, não há listener global de mousemove no document. Ambos rodam
   *  em `runOutsideAngular` porque só mutam CSS var (não há binding do
   *  Angular no `width`). Sem isso, o `@HostListener('document:mousemove')`
   *  disparava CD global a CADA pixel do mouse — trava telas pesadas
   *  (ex.: Pessoas com 4× 351 linhas). */
  private mouseMoveListener?: (e: MouseEvent) => void;
  private mouseUpListener?: () => void;

  ngOnInit(): void {
    this.subs.push(this.userService.getUserFromToken().subscribe(u => {
      this.user = u;
      this.imageError = false;
    }));
    this.subs.push(this.authService.directUserIsAdmin().subscribe(a => this.userIsAdmin = a));
    this.subs.push(this.helpdeskOverlay.isOpen$.subscribe(open => this.isSupportCardOpen = open));

    const saved = localStorage.getItem(AdminSidebarComponent.STORAGE_KEY);
    if (saved) {
      const n = parseInt(saved, 10);
      if (!isNaN(n)) {
        this.width = Math.max(AdminSidebarComponent.MIN_WIDTH, Math.min(AdminSidebarComponent.MAX_WIDTH, n));
      }
    }
    this.applyWidth();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.detachResizeListeners();
  }

  /** Propaga a largura via CSS custom property no host. `setProperty` é o
   *  único caminho confiável p/ custom props no Angular 11 (bindings de
   *  `[style.--var]` não propagam). A coluna `.admin-shell-nav` (= este host)
   *  consome em `flex-basis: var(--admin-sidebar-width)`. */
  private applyWidth(): void {
    this.el.nativeElement.style.setProperty('--admin-sidebar-width', this.width + 'px');
  }

  onResizeStart(event: MouseEvent): void {
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.width;
    event.preventDefault();
    event.stopPropagation();

    // Anexa listeners SÓ enquanto arrasta, fora do NgZone: nenhum CD dispara
    // a cada mousemove. `applyWidth` toca só CSS var (não há binding do
    // Angular no `width`), então não precisamos re-entrar na zona.
    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveListener = (e: MouseEvent) => {
        const dx = e.clientX - this.resizeStartX;
        this.width = Math.max(
          AdminSidebarComponent.MIN_WIDTH,
          Math.min(AdminSidebarComponent.MAX_WIDTH, this.resizeStartWidth + dx)
        );
        this.applyWidth();
      };
      this.mouseUpListener = () => {
        this.isResizing = false;
        localStorage.setItem(AdminSidebarComponent.STORAGE_KEY, String(this.width));
        this.detachResizeListeners();
      };
      document.addEventListener('mousemove', this.mouseMoveListener);
      document.addEventListener('mouseup', this.mouseUpListener);
    });
  }

  private detachResizeListeners(): void {
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
      this.mouseUpListener = undefined;
    }
  }

  /** Abre/fecha o card de helpdesk ancorado ao próprio botão (CDK Overlay,
   *  mesma stack do mat-menu do sino). Mesmo handler do sidebar moderno. */
  openSupportHelp(event?: MouseEvent): void {
    const target = (event?.currentTarget ?? event?.target) as HTMLElement | undefined;
    const trigger = target?.closest?.('.helpdesk-button') as HTMLElement | null;
    this.helpdeskOverlay.toggle(trigger ?? target);
  }
}
